import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { InputObject } from "../controls/default";
import { createSearchRequest } from "../hooks/useSearchRequest";
import { getPermissions } from "../repository/data.repository";
import { PermissionActionType } from "../repository/data.default";

export interface CompanyPermissionsForm {
  readonly companyRoleName: string;
  readonly permission: InputObject;
  readonly allowedAction: InputObject;
  readonly createdByUserUserName: string;
  readonly createdOn: string;
  readonly updatedByUserUserName: string;
  readonly updatedOn: string;
}

export const companyPermissionsViewColumns: DataColumn[] = [
  {
    title: "Role Name",
    key: "companyRoleName",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Permission Name",
    key: "permissionName",
    width: 192,
    sort: true,
  },
  {
    title: "Allowed Action",
    key: "allowedAction",
    width: 192,
    sort: true,
  },
  {
    title: "Created By",
    key: "createdByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 192,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Updated By",
    key: "updatedByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Updated On",
    key: "updatedOn",
    width: 192,
    format: "DD/MM/YYYY HH:mm",
    sort: true,
  },
];

const companyPermissionsQueryMappings: QueryFieldMapping[] = [
  { formField: "companyRoleName", operation: QueryOperation.Contains },
  { formField: "permission", queryField: "permissionUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "allowedAction", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "createdByUserUserName" },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "updatedByUserUserName" },
  { formField: "updatedOn", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapCompanyPermissionsViewQuery = (values: CompanyPermissionsForm): SearchQuery =>
  buildSearchQuery(values, companyPermissionsQueryMappings);

const allowedActionsRequest = (): Promise<InputObject[]> =>
  Promise.resolve(
    Object.entries(PermissionActionType).map(([value, display]) => ({
      display,
      value,
    })),
  );

export const companyPermissionsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        // Free-text role filter: there is no getCompanyRoles repository function (the company-role
        // catalog is exposed as getCompanyUserRoles -> /api/Data/CompanyRoles). Kept as a textbox
        // pending a dedicated getCompanyRoles search endpoint.
        { type: "textbox", name: "companyRoleName", label: "Role Name" },
        {
          type: "combobox",
          name: "permission",
          label: "Permission Name",
          request: createSearchRequest(getPermissions, "name"),
        },
        {
          type: "combobox",
          name: "allowedAction",
          label: "Allowed Action",
          request: allowedActionsRequest,
        },
        { type: "textbox", name: "createdByUserUserName", label: "Created By" },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
        { type: "textbox", name: "updatedByUserUserName", label: "Updated By" },
        { type: "rangetimepicker", name: "updatedOn", label: "Updated On" },
      ],
    },
  ],
};
